/**
 * The few shapes the M1a tenancy view needs on top of `workspaceUi`.
 *
 * WHY NOT A THIRD CARD SYSTEM. The repo already has two visual languages that
 * matter here: `tenancies/workspaceUi.jsx` (the global Mietverhältnisse
 * workspace) and `tenant/overview/overviewUi.jsx` (slice O1a). Adding a third
 * `Card` is how Finanzen ended up looking like its own product. So the card
 * itself comes from `workspaceUi` — this file adds only the pieces that module
 * has no equivalent for: the KPI tile row under the header, the status pill,
 * and the icon tile the reference layout puts beside each card title.
 *
 * All colour comes from semantic tokens, with one deliberate exception: the
 * pill and icon tones are literal Tailwind light/dark pairs, the same exception
 * `overviewUi` and `OverviewKpiCard` already make, because a token set cannot
 * express "five semantic hues" without inventing five more tokens.
 */
import React from "react";

/* ------------------------------------------------------------------ tones -- */

const PILL_TONES = {
  emerald:
    "border-emerald-500/30 bg-emerald-500/10 text-emerald-700 dark:text-emerald-400",
  sky: "border-sky-500/30 bg-sky-500/10 text-sky-700 dark:text-sky-400",
  amber: "border-amber-500/30 bg-amber-500/10 text-amber-700 dark:text-amber-400",
  rose: "border-rose-500/30 bg-rose-500/10 text-rose-700 dark:text-rose-400",
  slate: "border-border bg-muted text-muted-foreground",
};

const ICON_TONES = {
  blue: "bg-blue-50 text-blue-600 dark:bg-blue-500/10 dark:text-blue-400",
  emerald:
    "bg-emerald-50 text-emerald-600 dark:bg-emerald-500/10 dark:text-emerald-400",
  violet:
    "bg-violet-50 text-violet-600 dark:bg-violet-500/10 dark:text-violet-400",
  amber: "bg-amber-50 text-amber-600 dark:bg-amber-500/10 dark:text-amber-400",
  slate: "bg-muted text-muted-foreground",
};

/* ------------------------------------------------------------- components -- */

/**
 * A status word. One per tenancy in the header, never two competing ones.
 */
export function StatusPill({ tone = "slate", children, testId }) {
  return (
    <span
      data-testid={testId}
      className={`inline-flex items-center whitespace-nowrap rounded-full border px-2.5 py-0.5 text-[11px] font-semibold ${
        PILL_TONES[tone] || PILL_TONES.slate
      }`}
    >
      {children}
    </span>
  );
}

/** The rounded square that carries a card's or header's glyph. */
export function IconTile({ tone = "blue", size = "md", children }) {
  const box = size === "lg" ? "h-11 w-11 rounded-xl" : "h-8 w-8 rounded-lg";
  return (
    <span
      aria-hidden
      className={`flex ${box} shrink-0 items-center justify-center ${
        ICON_TONES[tone] || ICON_TONES.slate
      }`}
    >
      {children}
    </span>
  );
}

/**
 * One figure in the row under the tenancy header.
 *
 * `value` is already formatted by the caller — this component never formats a
 * number, because that is where "CHF 0.00 for a deposit nobody entered" came
 * from. A null value renders an em dash, and `hint` carries the qualifier that
 * keeps a figure honest (e.g. that a monthly amount is a sum of positions).
 */
export function KpiTile({ label, value, hint, icon, tone = "slate", testId }) {
  return (
    <div
      data-testid={testId}
      className="flex min-w-0 items-center gap-3 rounded-[10px] border border-border bg-card px-3 py-[10px]"
    >
      {icon ? <IconTile tone={tone}>{icon}</IconTile> : null}
      <div className="min-w-0">
        <p className="m-0 truncate text-[11.5px] leading-tight text-muted-foreground">
          {label}
        </p>
        <p className="m-0 mt-[2px] truncate text-[15px] font-semibold tabular-nums text-foreground">
          {value == null || value === "" ? "—" : value}
        </p>
        {hint ? (
          <p className="m-0 mt-[1px] truncate text-[10.5px] leading-tight text-muted-foreground">
            {hint}
          </p>
        ) : null}
      </div>
    </div>
  );
}

/** The four-up KPI row: 1 column on phones, 2 on tablets, 4 from `xl`. */
export function KpiRow({ children }) {
  return (
    <div className="grid grid-cols-1 gap-3 sm:grid-cols-2 xl:grid-cols-4">
      {children}
    </div>
  );
}

/* ----------------------------------------------------------------- icons --- */
/* Inline rather than a package: six glyphs do not justify a dependency, and
   `lucide-react` is already imported elsewhere in this tree for larger sets. */

const svg = {
  fill: "none",
  stroke: "currentColor",
  strokeWidth: 1.8,
  strokeLinecap: "round",
  strokeLinejoin: "round",
  viewBox: "0 0 24 24",
  className: "h-4 w-4",
};

export const HomeIcon = () => (
  <svg {...svg} className="h-5 w-5">
    <path d="M3 10.5 12 3l9 7.5" />
    <path d="M5 9.5V21h14V9.5" />
  </svg>
);

export const WalletIcon = () => (
  <svg {...svg}>
    <rect x="3" y="6" width="18" height="13" rx="2" />
    <path d="M3 10h18M16 14h2" />
  </svg>
);

export const ShieldIcon = () => (
  <svg {...svg}>
    <path d="M12 3 5 6v6c0 4 3 7 7 9 4-2 7-5 7-9V6l-7-3Z" />
  </svg>
);

export const FileIcon = () => (
  <svg {...svg}>
    <path d="M14 3H7a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h10a2 2 0 0 0 2-2V8l-5-5Z" />
    <path d="M14 3v5h5" />
  </svg>
);

export const KeyIcon = () => (
  <svg {...svg}>
    <circle cx="8" cy="14" r="4" />
    <path d="m11 11 8-8M17 5l2 2M14 8l2 2" />
  </svg>
);

export const UsersIcon = () => (
  <svg {...svg}>
    <path d="M16 20v-1.5a4 4 0 0 0-4-4H7a4 4 0 0 0-4 4V20" />
    <circle cx="9.5" cy="8" r="3.5" />
    <path d="M21 20v-1.5a4 4 0 0 0-3-3.87" />
  </svg>
);

export const FolderIcon = () => (
  <svg {...svg}>
    <path d="M3 7a2 2 0 0 1 2-2h4l2 2h8a2 2 0 0 1 2 2v8a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V7Z" />
  </svg>
);

export const CalendarIcon = () => (
  <svg {...svg} className="h-3.5 w-3.5">
    <rect x="3" y="5" width="18" height="16" rx="2" />
    <path d="M8 3v4M16 3v4M3 10h18" />
  </svg>
);
